import { useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";
import { logger } from "../../datadog/logger";
import { sendLoggingEvents, TrackingEvents } from "../../events";
import { UserType } from "../../types";
import { roomAtom, videoSDKAdapterAtom } from "../core/atoms";
import { localScreenShareContextAtom } from "../core/room/participant/media/atoms";
import { parseIdentity } from "../core/room/participant/utils";

/**
 * Core screen share hook - handles SDK interactions.
 * Session-specific concerns (PiP, confirmation modals) should be handled by caller.
 *
 * @param onError - Optional error handler for non-abort/non-permission errors
 * @returns Function that starts screen sharing and returns tracks
 */
export function useStartScreenShare(onError?: (error: Error) => void) {
  const getAdapter = useAtomCallback(
    useCallback((get) => get(videoSDKAdapterAtom), []),
  );
  const getRoom = useAtomCallback(useCallback((get) => get(roomAtom), []));

  const setLocalScreenShareContext = useSetAtom(localScreenShareContextAtom);

  return useCallback(async (): Promise<boolean> => {
    const room = getRoom();
    if (!room) {
      logger.error("Unable to share screen: room not found");
      return false;
    }

    try {
      const success = await getAdapter().startScreenShare(
        setLocalScreenShareContext,
        onError,
      );

      if (success) {
        const { userType } = parseIdentity(
          room.localParticipant?.identity ?? "",
        );
        sendLoggingEvents(TrackingEvents.ANY_SCREEN_SHARE);
        if (userType === UserType.CLIENT) {
          sendLoggingEvents(TrackingEvents.CLIENT_SCREEN_SHARE);
        }
        if (userType === UserType.PROVIDER) {
          sendLoggingEvents(TrackingEvents.PROVIDER_SCREEN_SHARE);
        }

        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  }, [getRoom, getAdapter, setLocalScreenShareContext, onError]);
}

/**
 * Core stop screen share hook - handles SDK interactions.
 * Session-specific cleanup should be handled by caller.
 *
 * @returns Function that stops screen sharing
 */
export function useStopScreenShare() {
  const getAdapter = useAtomCallback(
    useCallback((get) => get(videoSDKAdapterAtom), []),
  );

  const getLocalScreenShareContext = useAtomCallback(
    useCallback((get) => get(localScreenShareContextAtom), []),
  );
  const setLocalScreenShareContext = useSetAtom(localScreenShareContextAtom);

  return useCallback(async (): Promise<boolean> => {
    const success = await getAdapter().stopScreenShare(
      getLocalScreenShareContext,
      setLocalScreenShareContext,
    );

    return success;
  }, [getAdapter, getLocalScreenShareContext, setLocalScreenShareContext]);
}
